/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { $getRoot } from "lexical";
import "./ContentEditable.css";

import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import * as React from "react";
import useEditorStore from "store/useEditorStore";
import styled from "styled-components";
import { $createSlidParagraphNode, $isSlidParagraphNode } from "../nodes/SlidParagraphNode";

type Props = {
  className?: string;
  placeholderClassName?: string;
  placeholder?: any;
};

export default function LexicalContentEditable({ className, placeholder, placeholderClassName }: Props): JSX.Element {
  const { focusEditor } = useEditorStore();
  const { lexicalEditorRef, showEditorPlaceholder } = useEditorStore();

  const handleEditorBottomPaddingClick = () => {
    if (showEditorPlaceholder) return;
    // NOTE: when user clicks on the bottom area of the editor, insert a slid paragraph node if the last node is not an empty slid paragraph node
    lexicalEditorRef.current?.update(() => {
      const root = $getRoot();
      const lastChild = root.getLastChild();
      if ($isSlidParagraphNode(lastChild) && lastChild.getTextContent() === "") {
        lastChild.select();
      } else if (lastChild) {
        const paragraphNode = $createSlidParagraphNode();
        lastChild.insertAfter(paragraphNode);
        paragraphNode.select();
      }
    });
  };
  return (
    <Wrapper>
      <ContentEditable
        spellCheck={false}
        onFocus={() => {
          focusEditor();
        }}
        className={className ?? "ContentEditable__root"}
        aria-placeholder={placeholder}
        placeholder={<div className={placeholderClassName ?? "ContentEditable__placeholder"}>{placeholder}</div>}
      />
      <EditorBottomPaddingClickableArea onClick={handleEditorBottomPaddingClick} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const EditorBottomPaddingClickableArea = styled.div`
  height: 300px;
  width: 100%;
`;
